/**
 * AuthContext - Global authentication state management
 * Provides authentication status, user data, and auth methods across the app
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface User {
  id: string
  name: string
  username: string
  email: string
  avatar?: string
  bio?: string
  verified?: boolean
  isReporter?: boolean
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  login: (token: string, userData: User) => void
  logout: () => void
  updateUser: (userData: Partial<User>) => void
  checkAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)
  const navigate = useNavigate()

  // Check authentication status on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async (): Promise<boolean> => {
    try {
      setIsLoading(true)

      // Check if token exists
      const token = await api.getAuthToken()
      if (!token) {
        setIsAuthenticated(false)
        setUser(null)
        return false
      }

      // Try to get user profile from cache or API
      const profile = await api.getUserProfile()
      if (profile) {
        setUser(profile)
        setIsAuthenticated(true)

        // Load interaction states (likes, saves, reposts) from backend — non-blocking
        api.loadMyInteractions().catch(() => {})

        // Register FCM service worker silently — no permission prompt on page load
        import('../services/webPushService').then(({ default: webPush }) => {
          webPush.initialize().catch(() => {})
        })

        return true
      } else {
        // Token exists but profile fetch failed - clear auth
        await api.clearAuthToken()
        setIsAuthenticated(false)
        setUser(null)
        return false
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setIsAuthenticated(false)
      setUser(null)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const login = (token: string, userData: User) => {
    api.setAuthToken(token)
    localStorage.setItem('user_profile', JSON.stringify(userData))
    localStorage.setItem('isLoggedIn', 'true')
    setUser(userData)
    setIsAuthenticated(true)

    // Initialize web push notifications (non-blocking)
    import('../services/webPushService').then(({ default: webPush }) => {
      webPush.requestPermissionAndGetToken().catch(() => {})
    })
  }

  const logout = async () => {
    try {
      await api.logout()
      setUser(null)
      setIsAuthenticated(false)
      // Navigate to login page
      navigate('/login', { replace: true })
    } catch (error) {
      console.error('Logout error:', error)
      // Still clear state and navigate even if API call fails
      setUser(null)
      setIsAuthenticated(false)
      navigate('/login', { replace: true })
    }
  }

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...userData }
      setUser(updatedUser)
      localStorage.setItem('user_profile', JSON.stringify(updatedUser))
    }
  }

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        login,
        logout,
        updateUser,
        checkAuth
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
